import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    CalculatorDirection,
    DividendDeclaration,
    DistributionQuote,
    JournalEntry,
    LocaleCode,
} from '@/api/models';
import type { DividendAllocationOverride } from '@/api/handlers/resources/dividends';
import {
    useCommitDeclarationMutation,
    useInvalidatePostedDividendEntry,
    usePreviewDeclarationMutation,
} from '@/api/queries';
import { todayIso } from '@/pages/accounting/statements/statementMeta';
import {
    dividendErrorMessage,
    toAllocationRows,
    toQuoteFigures,
    type AllocationRow,
    type QuoteFigures,
} from '../../dividendsMeta';

type UseDeclareDistributionParams = {
    locale: LocaleCode;
    onClose: () => void;
};

export type DeclareModel = {
    direction: CalculatorDirection;
    amount: string;
    date: string;
    onDirectionChange: (direction: CalculatorDirection) => void;
    onAmountChange: (amount: string) => void;
    onDateChange: (date: string) => void;
    /** Per-shareholder gross overrides, keyed by shareholder id. */
    overrides: Record<string, string>;
    onOverrideChange: (shareholderId: string, gross: string) => void;
    onClearOverrides: () => void;
    hasOverrides: boolean;
    isPreviewing: boolean;
    isCommitting: boolean;
    error: string | null;
    rows: AllocationRow[];
    figures: QuoteFigures | null;
    quote: DistributionQuote | null;
    declaration: DividendDeclaration | null;
    hasPreview: boolean;
    /** True when the preview produced nothing to distribute. */
    isNilRun: boolean;
    /** Non-null when the distribution cannot be funded — the reason to disable. */
    blockedReason: 'EXCEEDS_DISTRIBUTABLE' | 'NIL' | null;
    onPreview: () => Promise<void>;
    /** Commit the declaration and open the draft it created. */
    onReview: () => Promise<void>;
    draft: JournalEntry | null;
    onDraftClose: () => void;
    onDraftSaved: (entry: JournalEntry) => void;
};

/**
 * Declaring a distribution.
 *
 * Two steps, and neither of them posts:
 *
 * 1. **Preview** asks the register for the per-shareholder split and the draft a
 *    commit would build. It writes nothing.
 * 2. **Review** *commits*: the server records the declaration, builds one
 *    balanced DRAFT entry and returns it whole, and that entry opens in the
 *    Journal Entry form. There is no third step — the server built the entry, so
 *    there is nothing for the client to post back and verify.
 *
 * The register moves only when a person posts that draft, under the journal's own
 * validation and period gating (a closed period is refused there — no special
 * case here). That is why posting, not committing, is what invalidates the feeds.
 *
 * The gate is on **charge**, not on the dividend: the gross-up means the CIT comes
 * out of the same retained earnings, so a dividend that looks affordable can still
 * be unfundable. `blockedReason` carries which of the two conditions applies so
 * the button's disabled state can say *why* rather than merely being grey.
 */
export const useDeclareDistribution = ({
    locale,
    onClose,
}: UseDeclareDistributionParams): DeclareModel => {
    const { t } = useTranslation('pages');
    const preview = usePreviewDeclarationMutation();
    const commit = useCommitDeclarationMutation();
    const invalidatePosted = useInvalidatePostedDividendEntry();

    const [direction, setDirection] =
        React.useState<CalculatorDirection>('BY_CHARGE');
    const [amount, setAmount] = React.useState('');
    const [date, setDate] = React.useState(todayIso);
    const [overrides, setOverrides] = React.useState<Record<string, string>>(
        {},
    );
    const [quote, setQuote] = React.useState<DistributionQuote | null>(null);
    const [declaration, setDeclaration] =
        React.useState<DividendDeclaration | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    /** The persisted DRAFT the commit created — the one the journal form opens. */
    const [draft, setDraft] = React.useState<JournalEntry | null>(null);

    /**
     * Any change to the inputs invalidates whatever was on screen. A preview
     * belongs to exactly one set of terms and must never be read against another
     * — the same rule the payroll run dialog follows for its period.
     */
    const invalidate = React.useCallback(() => {
        setQuote(null);
        setDeclaration(null);
        setError(null);
    }, []);

    const onDirectionChange = React.useCallback(
        (next: CalculatorDirection) => {
            invalidate();
            setDirection(next);
        },
        [invalidate],
    );
    const onAmountChange = React.useCallback(
        (next: string) => {
            invalidate();
            setAmount(next);
        },
        [invalidate],
    );
    const onDateChange = React.useCallback(
        (next: string) => {
            invalidate();
            setDate(next);
        },
        [invalidate],
    );
    const onOverrideChange = React.useCallback(
        (shareholderId: string, gross: string) => {
            invalidate();
            setOverrides((current) => ({ ...current, [shareholderId]: gross }));
        },
        [invalidate],
    );
    const onClearOverrides = React.useCallback(() => {
        invalidate();
        setOverrides({});
    }, [invalidate]);

    const overrideList = React.useMemo<DividendAllocationOverride[]>(
        () =>
            Object.entries(overrides)
                .filter(([, gross]) => gross.trim() !== '')
                .map(([shareholderId, gross]) => ({
                    shareholderId,
                    gross: gross.trim(),
                })),
        [overrides],
    );

    const distribution = React.useMemo(
        () => ({
            direction,
            amount: amount.trim(),
            date,
            allocations: overrideList,
            locale,
        }),
        [direction, amount, date, overrideList, locale],
    );

    const onPreview = React.useCallback(async () => {
        setError(null);
        const result = await preview.mutateAsync(distribution);
        if (!result.success) {
            setError(dividendErrorMessage(result.error, t));
            return;
        }
        setQuote(result.response.quote);
        setDeclaration(result.response.declaration);
    }, [preview, distribution, t]);

    /**
     * Record the declaration and open its draft.
     *
     * The preview's draft cannot be opened directly — it is a `DraftEntryPreview`
     * with no id, because nothing behind it exists yet. The commit is what turns
     * it into a real entry, and its response is the only place a saved draft
     * comes from. It still posts nothing.
     */
    const onReview = React.useCallback(async () => {
        setError(null);
        const result = await commit.mutateAsync(distribution);
        if (!result.success) {
            setError(dividendErrorMessage(result.error, t));
            return;
        }
        // `alreadyExisted` is not an error: the server handed back the
        // declaration these terms already had, which is the no-duplicates
        // guarantee working.
        setDraft(result.response.draft);
    }, [commit, distribution, t]);

    const onDraftClose = React.useCallback(() => {
        // The draft stays — it is persisted, and closing the form is not a
        // withdrawal. It can be found and posted from the journal.
        setDraft(null);
    }, []);

    const onDraftSaved = React.useCallback(
        (entry: JournalEntry) => {
            // There is no commit to make — the declaration was recorded when the
            // draft was created. But the register has to be told, and only on a
            // post: posting is what moves every accrued figure and the ledger
            // side of the tie-out. A save that left the entry a DRAFT moves
            // nothing, so it leaves the form open and changes nothing here.
            invalidatePosted(entry);
            if (entry.status !== 'POSTED') {
                return;
            }
            setDraft(null);
            onClose();
        },
        [invalidatePosted, onClose],
    );

    const rows = React.useMemo(
        () => (quote ? toAllocationRows(quote.lines, locale) : []),
        [quote, locale],
    );
    const figures = React.useMemo(
        () => (quote ? toQuoteFigures(quote.totals) : null),
        [quote],
    );

    const isNilRun = quote !== null && quote.totals.grossDividendTetri <= 0;

    return {
        direction,
        amount,
        date,
        onDirectionChange,
        onAmountChange,
        onDateChange,
        overrides,
        onOverrideChange,
        onClearOverrides,
        hasOverrides: overrideList.length > 0,
        isPreviewing: preview.isPending,
        isCommitting: commit.isPending,
        error,
        rows,
        figures,
        quote,
        declaration,
        hasPreview: quote !== null,
        isNilRun,
        blockedReason:
            quote === null
                ? null
                : quote.exceedsDistributable
                  ? 'EXCEEDS_DISTRIBUTABLE'
                  : isNilRun
                    ? 'NIL'
                    : null,
        onPreview,
        onReview,
        draft,
        onDraftClose,
        onDraftSaved,
    };
};
